import { useBrandContext } from '@/features/brands/brand-context'
import { IUrlPattern, UrlConfig } from '@/setup/routing/routing'
import { useParams } from 'next/navigation'
import { useMemo } from 'react'

type GetUrlParams<T extends IUrlPattern> = Parameters<(typeof UrlConfig)[T]['getUrl']>[0]

type OmitBrandSlug<T> = T extends { brandSlug: any } ? Omit<T, 'brandSlug'> : T

type BrandRouteParams<T extends IUrlPattern> = OmitBrandSlug<GetUrlParams<T>>

export const createBrandRouteGetter = (brandSlug: string) => {
    return <T extends IUrlPattern>(pattern: T) => {
        const originalRoute = UrlConfig[pattern]

        return {
            ...originalRoute,
            getUrl: (
                ...args: Record<string, never> extends BrandRouteParams<T>
                    ? [params?: BrandRouteParams<T>]
                    : [params: BrandRouteParams<T>]
            ) => {
                const params = args[0] || {}
                const fullParams = { brandSlug, ...params }

                // @ts-expect-error - Complex union types make this difficult to type perfectly, but it's safe at runtime
                return originalRoute.getUrl(fullParams)
            },
        }
    }
}

export const useBrandRoute = () => {
    const params = useParams()
    const { brandSlug } = useBrandContext()

    // Prefer the live URL segment (keeps the readable `slug-{uuid}` prefix in
    // every built URL); the context identity (the bare UUID) is the fallback
    // for consumers rendered outside a /brands/* route. Both resolve to the
    // same brand — only the UUID part of the segment carries identity.
    const segment = (params.brandSlug as string | undefined) || brandSlug

    const getRoute = useMemo(() => createBrandRouteGetter(segment), [segment])

    return {
        getRoute,
    }
}
